import React, { useState, useEffect } from "react";
import allApi from "../../../../api/allApi";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import { useLocalization } from "../../localization/LocalizationContext";
import { asList, isIPv4 } from "../../common/configValidation";

function IpSettings({networkId}) {
  const {localizationStrings} = useLocalization();
  const [deviceListing, setDeviceListing] = useState([]);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [serialNumber, setSerialNumber] = useState("");
  const [formError, setFormError] = useState("");
  const [isDirty, setIsDirty] = useState(false);
  const [initialFormFields, setInitialFormFields] = useState({});
  const [formFields, setFormFields] = useState({
    dhcpClientStatus: 1,
    ipAddress: "",
    subnetMask: "",
    gatewayAddress: "",
    primaryDnsAddress: "",
    secondaryDnsAddress: "",
  });

  useEffect(() => {
    getDeviceDetails(networkId);
  }, [networkId]);

  useEffect(() => {
    if (serialNumber) {
      getIpDetails(serialNumber);
    }
  }, [serialNumber]);

  const getDeviceDetails = async () => {
    try {
      const api = new allApi();
      const response = await api.getRequest(`api/wireless/deviceList?networkId=${networkId}`);
      if (response && response.response) {
        setDeviceListing(asList(response.response));
      }
    } catch (error) {
      console.error("Error fetching devices", error);
    }
  };

  const getIpDetails = async (serialNumber) => {
    try {
      const api = new allApi();
      const response = await api.getRequest(`api/http/ipSettings/${serialNumber}`);
      if (response) {
        const fields ={
          dhcpClientStatus: response.dhcpClientStatus || 1,
          ipAddress: response.ipAddress || "",
          subnetMask: response.subnetMask || "",
          gatewayAddress: response.gatewayAddress || "",
          primaryDnsAddress: response.primaryDnsAddress || "",
          secondaryDnsAddress: response.secondaryDnsAddress || "",
        }
        setFormFields(fields);
        setInitialFormFields(fields); 
        setIsDirty(false); 
      }
    } catch (error) {
      console.error("Error fetching IP setting values", error);
    }
  };

  const handleInputChange = (e) => {
    setSerialNumber(e.target.value);
  };

  const isStaticValid = () => {
    if (formFields.dhcpClientStatus === 1) return true;
    return (
      isIPv4(formFields.ipAddress) &&
      isIPv4(formFields.subnetMask) &&
      isIPv4(formFields.gatewayAddress) &&
      isIPv4(formFields.primaryDnsAddress) &&
      (!formFields.secondaryDnsAddress || isIPv4(formFields.secondaryDnsAddress))
    );
  };

  const inputSettings = async () => {
    if (!serialNumber) {
      setFormError("Select a serial number.");
      return;
    }
    if (!isStaticValid()) {
      setFormError("Enter valid IPv4 addresses for IP, mask, gateway, and DNS.");
      return;
    }
    try {
      setFormError("");
      const api = new allApi();
      const payload = {
        SerialNumber: serialNumber,
        dhcpClientStatus: formFields.dhcpClientStatus,
        ipAddr: formFields.ipAddress,
        netmaskAddr: formFields.subnetMask,
        gatewayAddr: formFields.gatewayAddress,
        priDnsAddr: formFields.primaryDnsAddress,
        sndDnsAddr: formFields.secondaryDnsAddress,
      };
      const response = await api.postRequest(`api/wireless/ipSettings?networkId=${networkId}`, "", payload);
      if (response) {
        setShowSuccessPopup(true);
        //console.log("IP settings set successfully", response);
      }
    } catch (error) {
      console.error("Error setting IP settings", error);
    }
  };

  const handleFieldChange = (field, value) => {
    setFormFields((prev) => ({ ...prev, [field]: value }));
    setIsDirty(true); 
  };

  const handleDhcpToggle = () => {
    setFormFields((prev) => ({
      ...prev,
      dhcpClientStatus: prev.dhcpClientStatus === 1 ? 0 : 1,
    }));
    setIsDirty(true);
  };

  const handleCancelPopUp = () => {
    setShowSuccessPopup(false);
  };

  const handleCancel = () => {
    setFormFields(initialFormFields);
    setIsDirty(false);
  };

  return (
    <div>
      <div className="tab-form w-scroll">
        <div className="col-lg-6">
          <label htmlFor="modelName" className="col-form-label">
            {localizationStrings?.serialNumber}
          </label>
          <select
            className="form-select"
            name="modelName"
            value={serialNumber}
            onChange={handleInputChange}
          >
            <option value="">{localizationStrings?.selectSerialNumber}</option>
            {deviceListing?.map((device, index) => (
              <option key={index} value={device.serialNumber}>
                {device.serialNumber}
              </option>
            ))}
          </select>
        </div>
        <div className="row mb-4 mt-3">
          <div className="col-lg-6">
            <div className="switch-box">
              <strong>{localizationStrings?.assignIp}</strong>
              <label className="ice-switch">
                <input
                  type="checkbox"
                  checked={formFields.dhcpClientStatus === 1}
                  onChange={handleDhcpToggle}
                />
                <span className="slider"></span>
              </label>
            </div>
          </div>
        </div>
        <div className="row mb-3">
          <div className="col-lg-4">
            <label htmlFor="ipaddress" className="col-form-label">
              {localizationStrings?.ipAddress}
            </label>
            <input
              type="text"
              className="form-control"
              value={formFields.ipAddress}
              onChange={(e) => handleFieldChange("ipAddress", e.target.value)}
              disabled={formFields.dhcpClientStatus === 1}
            />
          </div>
          <div className="col-lg-4">
            <label htmlFor="subnetmask" className="col-form-label">
              {localizationStrings?.subnetMask}
            </label>
            <input
              type="text"
              className="form-control"
              value={formFields.subnetMask}
              onChange={(e) => handleFieldChange("subnetMask", e.target.value)}
              disabled={formFields.dhcpClientStatus === 1}
            />
          </div>
        </div>
        <div className="row mb-3">
          <div className="col-lg-4">
            <label htmlFor="primaryDns" className="col-form-label">
              {localizationStrings?.primaryDns}
            </label>
            <input
              type="text"
              className="form-control"
              value={formFields.primaryDnsAddress}
              onChange={(e) => handleFieldChange("primaryDnsAddress", e.target.value)}
              disabled={formFields.dhcpClientStatus === 1}
            />
          </div>
          <div className="col-lg-4">
            <label htmlFor="secondaryDns" className="col-form-label">
              {localizationStrings?.secondaryDns}
            </label>
            <input
              type="text"
              className="form-control"
              value={formFields.secondaryDnsAddress}
              onChange={(e) => handleFieldChange("secondaryDnsAddress", e.target.value)}
              disabled={formFields.dhcpClientStatus === 1}
            />
          </div>
        </div>
        <div className="row mb-3">
          <div className="col-lg-4">
            <label htmlFor="gatewayAddress" className="col-form-label">
              {localizationStrings?.gatewayAddress}
            </label>
            <input
              type="text"
              className="form-control"
              value={formFields.gatewayAddress}
              onChange={(e) => handleFieldChange("gatewayAddress", e.target.value)}
              disabled={formFields.dhcpClientStatus === 1}
            />
          </div>
        </div>
        {formError && <p className="text-danger mt-2">{formError}</p>}
      </div>
      <div className="wifi-footer">
        <button type="button" className="btn btn-outline" onClick={handleCancel}>
          {localizationStrings?.cancel}
        </button>
        <button type="submit" className="text-btn primary-btn" onClick={()=>inputSettings(networkId)} disabled={!isDirty || !serialNumber || !isStaticValid()}>
          {localizationStrings?.save}
        </button>
      </div>
      <Modal
        show={showSuccessPopup}
        onHide={handleCancelPopUp}
        aria-labelledby="contained-modal-title-vcenter"
        centered
        className="inc-alert index-2000"
      >
        <span className="success-icon">
          <i className="icon done"></i>
        </span>
        <h3>{localizationStrings?.success}</h3>
        <p>{localizationStrings?.ipSuccess}</p>
        <Button onClick={handleCancelPopUp}>
          {localizationStrings?.ok}
        </Button>
      </Modal>
    </div>
  );
}

export default IpSettings;
